// ─────────────────────────────────────────────────────────────
// NewsWidget
//
// Reads cached news articles from the Supabase `news_cache` table.
// The cache is populated by the `news-poller` Edge Function, which
// calls Finnhub server-side and classifies each headline by trading
// impact (high/medium/low).
//
// Visual hierarchy is by impact:
//   - high   → red border-left, brighter headline, IMPACT pill
//   - medium → yellow border-left, normal headline, no pill
//   - low    → no border accent, slightly dimmed headline
//
// Schema (news_cache):
//   id (int8), title, description, source, link, published_at,
//   created_at, image_url, impact
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { C } from "../../lib/colors";
import { SUPABASE_URL, authFetch } from "../../lib/supabase";

const MAX_ITEMS  = 6;
const REFRESH_MS = 5 * 60 * 1000;  // refetch the cache every 5 min

export function NewsWidget() {
  const [newsItems,   setNewsItems]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchNews = async () => {
      try {
        // Order primarily by impact (high first), then by recency.
        // PostgREST treats 'high' > 'low' > 'medium' alphabetically, so we
        // can't sort by impact directly — instead fetch a few more than we
        // need and sort client-side.
        const params = new URLSearchParams({
          select: "id,title,description,source,link,published_at,created_at,impact",
          order:  "published_at.desc",
          limit:  String(MAX_ITEMS * 3),  // pull extra so client sort is meaningful
        });
        const res = await authFetch(
          `${SUPABASE_URL}/rest/v1/news_cache?${params.toString()}`
        );

        if (!res.ok) {
          throw new Error(`Supabase returned ${res.status}`);
        }

        const data = await res.json();
        if (cancelled) return;

        // Decode HTML entities in titles/descriptions (e.g. &#x2018;)
        const decode = (s) => {
          if (!s) return "";
          const t = document.createElement("textarea");
          t.innerHTML = s;
          return t.value;
        };

        const items = (data || [])
          .map(row => ({
            id:          row.id,
            title:       decode(row.title),
            description: decode(row.description),
            source:      row.source || "",
            link:        row.link || "#",
            impact:      row.impact || "low",
            time:        formatPublishedTime(row.published_at),
          }))
          // Client-side sort: impact desc, then published_at handled by API order
          .sort((a, b) => impactRank(b.impact) - impactRank(a.impact))
          .slice(0, MAX_ITEMS);

        setNewsItems(items);
        setLoading(false);
        setError(null);
        const newestCreated = data.reduce(
          (latest, r) => {
            const t = new Date(r.created_at);
            return !latest || t > latest ? t : latest;
          },
          null
        );
        setLastUpdated(newestCreated || new Date());
      } catch (err) {
        if (cancelled) return;
        console.error("[NewsWidget] fetch failed:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchNews();
    const interval = setInterval(fetchNews, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <SectionTitle>News Feed</SectionTitle>
          <div style={{ fontSize: 10, color: C.muted, marginTop: -4, fontStyle: "italic" }}>
            Filtered for trading relevance
          </div>
        </div>
        <div style={{ fontSize: 10, color: C.muted }}>Updated: {formatLastUpdated(lastUpdated)}</div>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 12 }}>Loading…</div>
      ) : newsItems.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {newsItems.map((item) => <NewsRow key={item.id} item={item} />)}
        </div>
      ) : (
        <div style={{ color: C.muted, fontSize: 12 }}>
          {error
            ? <>⚠️ Could not load news ({error})</>
            : "No news in the cache yet"}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// NewsRow — per-article presentation with impact-based hierarchy
// ─────────────────────────────────────────────────────────────

function NewsRow({ item }) {
  const isHigh   = item.impact === "high";
  const isMedium = item.impact === "medium";

  // Visual treatment per impact tier:
  //   high   → red left border, slight red background tint, bold-bright title
  //   medium → yellow left border, plain background, normal title
  //   low    → no border accent, slightly dimmed background+title
  const accentColor = isHigh ? C.red : isMedium ? C.yellow : "transparent";
  const bgColor     = isHigh ? C.red + "10" : C.border + "30";
  const titleColor  = isHigh || isMedium ? C.text : C.sub;
  const titleWeight = isHigh ? 600 : 400;
  const titleOpacity = isHigh ? 1 : isMedium ? 0.95 : 0.75;

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        padding: "10px 12px",
        background: bgColor,
        borderRadius: 6,
        borderLeft: isHigh || isMedium ? `3px solid ${accentColor}` : "none",
        // Add equivalent left padding when there's no border so all rows align
        paddingLeft: isHigh || isMedium ? 12 : 15,
        cursor: "pointer",
        textDecoration: "none",
        display: "block",
      }}
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 9, color: C.muted, fontFamily: "monospace" }}>{item.time}</div>
        <div style={{ fontSize: 9, color: C.blue, fontWeight: 600 }}>{item.source}</div>
        {isHigh && (
          <div style={{
            fontSize: 8, fontWeight: 700, color: C.red,
            background: C.red + "20",
            padding: "1px 6px", borderRadius: 3,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}>
            High Impact
          </div>
        )}
      </div>
      <div style={{
        fontSize: 11,
        color: titleColor,
        opacity: titleOpacity,
        fontWeight: titleWeight,
        lineHeight: "1.35",
      }}>
        {item.title.length > 90 ? item.title.substring(0, 90) + "…" : item.title}
      </div>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function impactRank(impact) {
  if (impact === "high")   return 2;
  if (impact === "medium") return 1;
  return 0;
}

function formatPublishedTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const mins   = Math.floor(diffMs / 60000);
  if (mins < 1)    return "just now";
  if (mins < 60)   return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24)  return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7)    return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatLastUpdated(date) {
  if (!date) return "Never";
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}
